import React, { useEffect } from 'react'
import styles from './styles/h2h.module.scss'
import H2HStats from './H2HStats'
import H2HMatchHistory, { BlurredMatchHistory } from './H2HMatchHistory'
import { useTranslation } from 'react-i18next'
import H2HCommonCompetitors from './H2HCommonCompetitors'
import H2HInfoHeader from './H2HInfoHeader'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { getVsInfo as getVsInfoAction } from 'actions/h2h-actions'
import HistoryLineChart from 'components/chart/HistoryLineChart/HistoryLineChart'
import { capitalizeEachWord } from 'utils'
import WTNSpinner from 'components/generic/WTNSpinner'
import InfoBoxContainer from 'components/generic/InfoBoxContainer'
import { LineChartLabel, MatchUpTypeEnum, Player } from 'types/global'
import cx from 'classnames'
import BlurredWtnHistoryChart from 'components/profile/locked-panels/BlurredWtnHistoryChart'

interface Props {
  playerIds: [string, string]
  showWtnHistory?: boolean
  hideResultsForAnonUser?: boolean
}

const HeadToHead = ({
  playerIds,
  showWtnHistory = false,
  hideResultsForAnonUser = false,
}: Props) => {
  const { t: tCharts } = useTranslation('strCharts')
  const { t: tData } = useTranslation('strData')
  const { t: tProfile } = useTranslation('strProfile')
  const { t: tSearchBar } = useTranslation('strSearchBar')

  const dispatch = useDispatch()
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const vsInfo = useSelector((state: AppState) => state.h2h.vsInfo)
  const vsInfoLoading = useSelector((state: AppState) => state.h2h.vsInfoLoading)
  const hideMatchHistory = hideResultsForAnonUser && !isLoggedIn

  const types = [
    { key: MatchUpTypeEnum.SINGLES, label: tCharts('singles') },
    { key: MatchUpTypeEnum.DOUBLES, label: tCharts('doubles') },
  ]

  const mapKeys = (players: Player[]) => {
    let keys: LineChartLabel[] = []
    players.forEach((player, index) => {
      for (let i = 0; i < 2; i++) {
        keys.push({
          type: types[i].key,
          name: `${capitalizeEachWord(player.fullName) || tData('noPlayerName')} (${
            types[i].label
          })`,
          keyCol: index * 2 + i,
        })
      }
    })
    return keys
  }

  useEffect(() => {
    if (playerIds.every(Boolean)) {
      dispatch(getVsInfoAction(playerIds))
    }
  }, [dispatch, playerIds])

  return (
    <>
      {vsInfoLoading && (
        <InfoBoxContainer className={styles.flexCenter}>
          <WTNSpinner width={85} />
        </InfoBoxContainer>
      )}
      {!vsInfo && !vsInfoLoading && (
        <div className={cx(styles.noH2hSelection, styles.flexCenter)}>
          <p className={styles.noH2hSelectionText}>{tSearchBar('h2hSingleSearchCompare')}</p>
        </div>
      )}

      {vsInfo && !vsInfoLoading && (
        <>
          <InfoBoxContainer className={styles.HeadToHeadHeader}>
            <H2HInfoHeader {...vsInfo} />
            <div className={styles.statsTable}>
              <H2HStats {...vsInfo} isLoggedIn={isLoggedIn} />
            </div>
          </InfoBoxContainer>

          <InfoBoxContainer>
            {showWtnHistory ? (
              <HistoryLineChart
                title={tProfile('wtnHistory')}
                playerIds={playerIds}
                labels={vsInfo ? mapKeys(vsInfo.players) : []}
              />
            ) : (
              <BlurredWtnHistoryChart />
            )}
          </InfoBoxContainer>

          <InfoBoxContainer>
            {hideMatchHistory ? <BlurredMatchHistory /> : <H2HMatchHistory playerIds={playerIds} />}
          </InfoBoxContainer>

          <InfoBoxContainer>
            <H2HCommonCompetitors players={vsInfo.players} />
          </InfoBoxContainer>
        </>
      )}
    </>
  )
}

export default HeadToHead
